import type Good from '@/core/model/good';
import {defineStore} from 'pinia';
import {computed, ref, watch} from 'vue';
import LocalStorageDecorator from '@/store/storage/local-storage-decorator';
import { useAnalytics } from '@/composables/useAnalytics';
import { useNotification } from '@/composables/useNotification';

const MAX_GOODS_PER_CLASS = 100;
export interface ProductStore {
    goods: Good[];
}

export const useProductStore = defineStore('productStore', () => {
    const goods = ref<Good[]>([])
    const goodClassNumberExeeded = ref<number[]>([])
    const analytics = useAnalytics();
    const notification = useNotification();

    function resetData() {
        goods.value = [];
    }

    function importData(data: ProductStore) {
        if (data.goods) {
            goods.value = data.goods;
        }
    }

    function exportData() {
        return {
            goods: goods.value
        };
    }

    const toggleGood = (good: Good) => {
        const goodIndex = goods.value.findIndex(item => item.externalId === good.externalId);

        let isAdded = true;
        if (goodIndex > -1) {
            //remove good
            goods.value.splice(goodIndex, 1);
            isAdded = false;
        } else {
            //add good
            if (goodClassNumberExeeded.value.includes(good.classNumber)) {
                return;
            }

            goods.value.push(good);
        }

        setExceedClass(good.classNumber, isAdded);
    }

    const removeClass = (classNumber: number) => {
        goods.value = goods.value.filter(item => item.classNumber !== classNumber);
        removeExceedClass(classNumber);
    }

    const classes = computed(() => {
        let goodClasses = goods.value.map(good => good.classNumber)

        goodClasses = [...new Set(goodClasses)];

        return goodClasses
    })

    const totalClasses = computed(() => {
        return classes.value.length
    })

    const totalGoods = computed(() => {
        return goods.value.length;
    })

    const isProductStepValid = computed(() => {
        return totalGoods.value > 0;
    })

    watch(isProductStepValid, (valid) => {
        if (valid) {
            analytics.sendEvent('card_completed_goods');
        }
    });

    function setExceedClass(classNumber: number, isAdded: boolean) {
        const goodByClass = goods.value.filter(good => good.classNumber === classNumber);

        if (goodByClass.length >= MAX_GOODS_PER_CLASS) {
            if (isAdded && !goodClassNumberExeeded.value.includes(classNumber)) {
                //if goods by class reach 100, add class number to exeeded
                addExceedClass(classNumber);
            }
        } else {
            removeExceedClass(classNumber);
        }
    };

    function removeExceedClass(classNumber: number) {
        goodClassNumberExeeded.value = goodClassNumberExeeded.value.filter(item => item !== classNumber);
    }

    function addExceedClass(classNumber: number) {
        goodClassNumberExeeded.value.push(classNumber);

        notification.showInfoNotification(
            `You have selected 100 items in class ${classNumber}. </br>You can not add more items from class ${classNumber}.`
        );
    }

    return {goodClassNumberExeeded, toggleGood, removeClass, goods, classes, totalClasses, totalGoods, isProductStepValid, resetData, importData, exportData};
},{
    persist: {
        storage: LocalStorageDecorator
    },
});
